import { CustomButton } from "#components/ui/CustomButton.tsx";
import { type BackgroundColorValue, type ColorValue, Flex, Text, useColorSchemeValue } from "@hopper-ui/components";
import { ArrowRightIcon, CheckmarkIcon, EnterIcon } from "@hopper-ui/icons";
import "./OptionRow.css";

interface Props {
    label: string;
    index: number;
    onPress: () => void;
    isDisabled?: boolean;
    /** Defined only in selectable (checkmark) mode. `true` = selected, `false` = not yet selected. */
    isSelected?: boolean;
    isLast?: boolean;
}

/** Full-width selectable option row shared by the interactive-question and tool-approval prompts. */
export function OptionRow({ label, index, onPress, isDisabled, isSelected, isLast }: Props) {
    const textColor = useColorSchemeValue(
        "neutral",
        "neutral"
    ) as ColorValue;

    const numberTextColor = useColorSchemeValue(
        "neutral-weak",
        "neutral-weak"
    ) as ColorValue;

    const selectedNumberBgColor = "core_orchid-bloom-600" as BackgroundColorValue;
    const selectedNumberTextColor = "core_samoyed" as ColorValue;

    const selectedBgColor = useColorSchemeValue(
        "core_orchid-bloom-50",
        "core_orchid-bloom-800"
    ) as BackgroundColorValue;

    const indicatorBorderColor = useColorSchemeValue(
        "rock-200",
        "rock-500"
    ) as ColorValue;

    const backgroundColorHover = useColorSchemeValue(
        "core_rock-50",
        "core_rock-800"
    ) as BackgroundColorValue;

    const isSelectableMode = isSelected !== undefined;

    const activeBgColor = isSelected ? selectedBgColor : "transparent";

    const badgeBgColor: BackgroundColorValue | undefined = isSelected === true
        ? selectedNumberBgColor
        : undefined;

    return (
        <CustomButton
            backgroundColor={activeBgColor}
            paddingLeft="inset-md"
            paddingRight="inset-md"
            paddingY="inset-xs"
            border="none"
            borderRadius="rounded-md"
            backgroundColorHover={backgroundColorHover}
            width="100%"
            minHeight="core_480"
            className="wai-option-row"
            onPress={onPress}
            isDisabled={isDisabled}
            aria-pressed={isSelectableMode ? isSelected : undefined}
        >
            <Flex
                alignItems="center"
                gap="inline-md"
                textAlign="left"
                width="100%"
            >
                <Flex
                    backgroundColor={badgeBgColor}
                    borderRadius="pill"
                    width="core_240"
                    height="core_240"
                    alignItems="center"
                    justifyContent="center"
                    shrink="0"
                    color={isSelected === true ? selectedNumberTextColor : numberTextColor}
                    className="wai-option-row-number"
                    UNSAFE_border={isSelected
                        ? "0.0625rem solid var(--hop-orchid-bloom-600)"
                        : `0.0625rem solid var(--hop-${indicatorBorderColor})`}
                >
                    {isSelected === true && <CheckmarkIcon size="sm" />}
                    {!isSelectableMode && <Text size="xs">{index + 1}</Text>}
                </Flex>
                <Flex grow={1}>
                    <Text size="sm" color={textColor}>
                        {label}
                    </Text>
                </Flex>
                {!isSelectableMode && (isLast
                    ? <EnterIcon color="neutral-weak" size="sm" flexShrink={0} className="wai-option-row-arrow" />
                    : <ArrowRightIcon color="neutral-weak" size="sm" flexShrink={0} className="wai-option-row-arrow" />
                )}
            </Flex>
        </CustomButton>
    );
}
